import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Download, UserCheck, LogIn, LogOut, Clock, Search, ArrowUpDown, FileText } from 'lucide-react';
import { generateEventReportPDF } from '@/utils/pdfGenerator';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckInStats({ eventId }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [eventData, setEventData] = useState(null);
  const { tenant } = useAuth();

  useEffect(() => {
    fetchStats();
    // Refresh every 10 seconds
    const interval = setInterval(fetchStats, 10000);
    return () => clearInterval(interval);
  }, [eventId]);

  const fetchStats = async () => {
    try {
      const [statsRes, eventRes] = await Promise.all([
        axios.get(`${API_URL}/events/${eventId}/checkin-stats`),
        axios.get(`${API_URL}/events/${eventId}`)
      ]);
      setStats(statsRes.data);
      setEventData(eventRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching stats:', error);
      setLoading(false);
    }
  };

  const exportStats = () => {
    if (!stats) return;

    const csv = [
      ['Nome', 'Email', 'Totale Entrate', 'Totale Uscite', 'Primo Ingresso', 'Ultimo Scan', 'Attualmente Dentro', 'Log Completo'],
      ...Object.values(stats.participants).map(p => [
        p.holder_name,
        p.holder_email,
        p.total_entries,
        p.total_exits,
        p.first_entry ? new Date(p.first_entry).toLocaleString('it-IT') : '-',
        p.last_scan ? new Date(p.last_scan).toLocaleString('it-IT') : '-',
        p.currently_inside ? 'Sì' : 'No',
        p.scan_log.map(log => `${log.type === 'entry' ? 'ENTRATA' : 'USCITA'} - ${new Date(log.timestamp).toLocaleTimeString('it-IT')}`).join('; ')
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `checkin-stats-${eventId}-${Date.now()}.csv`;
    a.click();
    toast.success('Statistiche esportate!');
  };

  const exportPDF = async () => {
    if (!stats || !eventData) return;
    
    try {
      const fileName = await generateEventReportPDF(eventData, stats, tenant);
      toast.success(`Report PDF generato: ${fileName}`);
    } catch (error) {
      toast.error('Errore durante la generazione PDF');
      console.error(error);
    }
  };

  // Filter and sort participants
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getFilteredAndSortedParticipants = () => {
    if (!stats) return [];
    
    let participants = Object.values(stats.participants).filter(p => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        p.holder_name.toLowerCase().includes(query) ||
        p.holder_email.toLowerCase().includes(query)
      );
    });

    if (sortField) {
      participants.sort((a, b) => {
        let aVal, bVal;
        if (sortField === 'name') {
          aVal = a.holder_name;
          bVal = b.holder_name;
        } else if (sortField === 'entries') {
          aVal = a.total_entries;
          bVal = b.total_entries;
        } else if (sortField === 'exits') {
          aVal = a.total_exits;
          bVal = b.total_exits;
        } else if (sortField === 'status') {
          aVal = a.currently_inside ? 1 : 0;
          bVal = b.currently_inside ? 1 : 0;
        }
        
        if (sortDirection === 'asc') {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });
    }

    return participants;
  };


  if (loading) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <p className="text-gray-600">Caricamento statistiche...</p>
        </CardContent>
      </Card>
    );
  }

  if (!stats || stats.total_participants === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <UserCheck className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun check-in ancora</h3>
          <p className="text-gray-600">Le statistiche appariranno qui appena qualcuno farà check-in</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Totale Partecipanti</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats.total_participants}</p>
              </div>
              <UserCheck className="w-12 h-12 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Attualmente Dentro</p>
                <p className="text-3xl font-bold text-green-600 mt-2">{stats.currently_inside}</p>
              </div>
              <LogIn className="w-12 h-12 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Attualmente Fuori</p>
                <p className="text-3xl font-bold text-gray-600 mt-2">{stats.total_participants - stats.currently_inside}</p>
              </div>
              <LogOut className="w-12 h-12 text-gray-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Table */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center gap-4">
            <CardTitle>Dettaglio Partecipanti</CardTitle>
            <div className="flex items-center gap-2 flex-1 max-w-md">
              <Search className="w-4 h-4 text-gray-400" />
              <Input
                placeholder="Cerca per nome o email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={exportStats} variant="outline" size="sm">
                <Download className="w-4 h-4 mr-2" />
                Esporta CSV
              </Button>
              <Button onClick={exportPDF} variant="outline" size="sm">
                <FileText className="w-4 h-4 mr-2" />
                Report PDF
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-1">
                      Partecipante
                      <ArrowUpDown className="w-3 h-3" />
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center gap-1">
                      Stato
                      <ArrowUpDown className="w-3 h-3" />
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('entries')}
                  >
                    <div className="flex items-center gap-1">
                      Entrate
                      <ArrowUpDown className="w-3 h-3" />
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('exits')}
                  >
                    <div className="flex items-center gap-1">
                      Uscite
                      <ArrowUpDown className="w-3 h-3" />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Primo Ingresso</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ultimo Scan</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Log</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {getFilteredAndSortedParticipants().map((participant, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{participant.holder_name}</div>
                      <div className="text-sm text-gray-500">{participant.holder_email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        participant.currently_inside ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {participant.currently_inside ? '🟢 Dentro' : '⚪ Fuori'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1 text-green-600 font-semibold">
                        <LogIn className="w-4 h-4" />
                        {participant.total_entries}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1 text-blue-600 font-semibold">
                        <LogOut className="w-4 h-4" />
                        {participant.total_exits}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {participant.first_entry ? new Date(participant.first_entry).toLocaleString('it-IT') : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {participant.last_scan ? new Date(participant.last_scan).toLocaleString('it-IT') : '-'}
                      </div>
                      {participant.last_scan_type && (
                        <span className={`text-xs ${
                          participant.last_scan_type === 'entry' ? 'text-green-600' : 'text-blue-600'
                        }`}>
                          {participant.last_scan_type === 'entry' ? 'Ingresso' : 'Uscita'}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <details className="text-sm">
                        <summary className="cursor-pointer text-blue-600 hover:text-blue-800">
                          Vedi log ({participant.scan_log.length} scan)
                        </summary>
                        <div className="mt-2 space-y-1 pl-4 border-l-2 border-gray-200">
                          {participant.scan_log.map((log, logIdx) => (
                            <div key={logIdx} className="text-xs text-gray-600">
                              <span className={`font-semibold ${
                                log.type === 'entry' ? 'text-green-600' : 'text-blue-600'
                              }`}>
                                {log.type === 'entry' ? '→ ENTRATA' : '← USCITA'}
                              </span>
                              {' '}{new Date(log.timestamp).toLocaleString('it-IT')}
                            </div>
                          ))}
                        </div>
                      </details>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
